import { Calendar } from 'primereact/calendar';
import type { FormEvent } from 'primereact/ts-helpers';
import type { FormikErrors, FormikTouched } from 'formik';
import React from 'react';

import { convertDateToClientZuluTime } from '../../../utils/date.helper';
import { convertDateToLocaleString } from '../../../utils/Helpers';
import PFFormikError from '../PFPrime/PFFormikError';

type PFCalendarProps<FormValues> = {
  id?: string;
  value?: Date | string | null;
  onChange: (_e: FormEvent<Date | string | null>) => void;
  placeholder?: string;
  name?: keyof FormValues;
  disabled?: boolean;
  className?: string;
  showTime?: boolean;
  hourFormat?: '12' | '24';
  appendTo?: 'self' | HTMLElement | null;
  minDate?: Date | null;
  maxDate?: Date | null;
  dateFormat?: string;
  isClientTimeZone?: boolean;
  label?: string;
  errors?: FormikErrors<FormValues>;
  touched?: FormikTouched<FormValues>;
  handleBlur?: {
    (_e: React.FocusEvent<HTMLElement>): void;
    <T = string | unknown>(
      _fieldOrEvent: T
    ): T extends string ? (_e: unknown) => void : void;
  };
};

export default function PFCommonCalendar<FormValues>({
  id,
  value,
  onChange,
  placeholder,
  name,
  disabled,
  className,
  showTime,
  hourFormat,
  appendTo = 'self',
  minDate,
  maxDate,
  dateFormat,
  isClientTimeZone,
  label,
  errors,
  touched,
  handleBlur,
  ...props
}: PFCalendarProps<FormValues>) {
  const calendarValue: Date | null = value
    ? typeof value === 'string'
      ? new Date(isClientTimeZone ? convertDateToLocaleString(value) : value)
      : value
    : null;

  return (
    <>
      <span className="p-float-label w-full">
        <Calendar
          id={id || ''}
          value={calendarValue}
          name={name ? String(name) : ''}
          onChange={(e: FormEvent<Date | string | null>) => {
            const updatedValue =
              e.value && isClientTimeZone
                ? convertDateToClientZuluTime(e.value as Date)
                : e.value;

            onChange({
              ...e,
              value: updatedValue,
              target: {
                ...e.target,
                value: updatedValue,
              },
            });
          }}
          className={`w-full ${className || ''}`}
          placeholder={placeholder || ''}
          appendTo={appendTo}
          disabled={!!disabled}
          showTime={showTime}
          hourFormat={hourFormat}
          minDate={minDate ?? undefined}
          maxDate={maxDate ?? undefined}
          dateFormat={dateFormat || 'mm-dd-yy'}
          inputClassName="p-inputtext-sm"
          hideOnDateTimeSelect
          onBlur={handleBlur}
          {...props}
        />

        {label && <label htmlFor={id}>{label}</label>}
      </span>

      <PFFormikError touched={touched} errors={errors} field={String(id)} />
    </>
  );
}
